import React from 'react';
import Calendar from 'react-calendar';
import moment from 'moment/moment';

import styled from 'styled-components';
import { PartnerScheduleAvailableDate } from '../../apis/models/partner-schedules/res/PartnerScheduleAvailableDate';
import strings from '../../constants/strings.constants';
import useLanguageCodeStore from '../../store/useLanguageCodeStore';
import colors from '../../constants/colors.constants';

export interface ReservationCalendarProps {
  availableDates?: Record<string, PartnerScheduleAvailableDate>;
  onDateSelect?: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
  loading?: boolean;
  value?: Date;
}

function ReservationCalendar(props: ReservationCalendarProps) {
  const {
    loading = false,
    availableDates = {},
    onDateSelect,
    onMonthChange,
    value,
  } = props;
  const { languageCode } = useLanguageCodeStore();

  return (
    <StyledCalendar
      $tileColor={colors.slate800}
      value={value}
      calendarType="gregory"
      locale={languageCode}
      navigationLabel={({ date }) => {
        return moment(date).format('YYYY.MM');
      }}
      tileClassName={(args) => {
        const { date } = args;
        // 공휴일 표기
        const momentDateString = moment(date).format('YYYY-MM-DD');
        const targetDate = availableDates[momentDateString];
        if (targetDate) {
          const { holiday } = targetDate;
          if (holiday) {
            return 'react-calendar__month-view__days__day--weekend';
          }
        }
        if (date.getDay() === 6) {
          return 'react-calendar__month-view__days__day--weekend-saturday';
        }
      }}
      tileDisabled={(args) => {
        const { date } = args;

        if (loading) return true;
        const momentDate = moment(date);
        const dateString = momentDate.format('YYYY-MM-DD');
        const availableDateInfo = availableDates[dateString];
        if (availableDateInfo) {
          const { isWork, canReserve } = availableDateInfo;
          return !isWork || !canReserve;
        }

        return false;
      }}
      onChange={(value) => {
        if (value) {
          if (onDateSelect) onDateSelect(value as Date);
        }
      }}
      onActiveStartDateChange={(args) => {
        const { activeStartDate } = args;
        if (onMonthChange && activeStartDate) {
          onMonthChange(activeStartDate);
        }
      }}
      view="month"
      defaultActiveStartDate={new Date()}
      minDate={new Date()}
      goToRangeStartOnSelect={false}
      formatDay={(locale, date) => {
        return date.getDate().toString();
      }}
      tileContent={(args) => {
        const { date } = args;
        if (date) {
          const momentDate = moment(date);
          const dateString = momentDate.format('YYYY-MM-DD');
          const availableDateInfo = availableDates[dateString];
          let dateExtraText = '';
          if (availableDateInfo) {
            const { holiday, isWork, reason } = availableDateInfo;
            if (!isWork) {
              dateExtraText = '';
            }
            if (reason) {
              dateExtraText = reason;
            } else if (holiday && isWork) {
              dateExtraText = strings.normalBusiness;
            } else if (holiday && !isWork) {
              dateExtraText = holiday;
            }
          }
          return (
            <div
              style={{
                position: 'relative',
              }}
            >
              <div
                style={{
                  bottom: -14,
                  left: 0,
                  position: 'absolute',
                  fontSize: 8,
                  width: '100%',
                  maxHeight: '20px',
                }}
              >
                <span>{dateExtraText}</span>
              </div>
            </div>
          );
        }
      }}
    />
  );
}

export default ReservationCalendar;

const StyledCalendar = styled(Calendar)<{ $tileColor: string }>`
  .react-calendar__tile--active {
    background: ${(props) => {
      return props.$tileColor;
    }};
    color: white;
  }

  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: ${(props) => {
      return props.$tileColor;
    }};
  }
`;
